/**
 * "Hide forecasts for this subscription" — a per-subscription, per-browser
 * view preference.
 *
 * A forecast pill is an *assumption* (renewals continue till the cap), and for
 * a subscription the owner has decided not to renew again that assumption is
 * noise. Hiding it here keeps the API generic: the same forecast serves
 * everybody, and what is noise is a matter of taste, not of data.
 *
 * Storage mirrors the other view prefs (`use-persisted-flag.ts`): read
 * tolerantly, write in an effect, survive a storage that is not there.
 */

import { useCallback, useEffect, useState } from 'react';

export const FORECAST_HIDDEN_KEY = 'payments.forecastHiddenSubscriptions';

function readHidden(): string[] {
  try {
    const raw = window.localStorage.getItem(FORECAST_HIDDEN_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

export function useHiddenForecasts(): [
  ReadonlySet<string>,
  (subscriptionId: string) => void,
  () => void,
] {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set(readHidden()));

  useEffect(() => {
    try {
      window.localStorage.setItem(FORECAST_HIDDEN_KEY, JSON.stringify([...hidden]));
    } catch {
      // Unpersistable is not unusable — keep the in-memory state.
    }
  }, [hidden]);

  const toggle = useCallback((subscriptionId: string) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(subscriptionId)) next.delete(subscriptionId);
      else next.add(subscriptionId);
      return next;
    });
  }, []);

  const showAll = useCallback(() => setHidden(new Set()), []);

  return [hidden, toggle, showAll];
}
